import { useCallback, useEffect, useRef, useState } from "react";

// Presentation-only rotation; stops while interacting or when the hero is hidden.
export const useHeroRotation = (count, delay = 5000) => {
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [hidden, setHidden] = useState(document.hidden);
  useEffect(() => {
    const update = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", update);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = event => { if (event.matches) setPaused(true); };
    motion.addEventListener("change", onMotion);
    return () => { document.removeEventListener("visibilitychange", update); motion.removeEventListener("change", onMotion); observer.disconnect(); };
  }, []);
  const running = !paused && !hovered && !focused && visible && !hidden;
  useEffect(() => {
    if (!running || count < 2) return;
    const timer = setTimeout(() => setIndex(i => (i + 1) % count), delay);
    return () => clearTimeout(timer);
  }, [index, count, delay, running]);
  const select = useCallback(next => { setIndex((next + count) % count); setPaused(true); }, [count]);
  const toggle = () => { setPaused(value => !value); setFocused(false); };
  return {
    ref, index, paused, running, select, toggle,
    interaction: {
      onPointerEnter: event => { if (event.pointerType === "mouse") setHovered(true); },
      onPointerLeave: () => setHovered(false),
      onFocusCapture: () => setFocused(true),
      onBlurCapture: event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); },
      onTouchStart: event => { if (!event.target.closest('[data-testid="hero-slide-toggle"]')) setPaused(true); },
    },
  };
};